<template>
	<div class="appView">
		<Sidebar />
		<section class="exportDataContainer">
			<main class="mainContent">
				<section class="leftContent">
					<div class="navBar">
						<Hamburger />
						<div class="title">
							Export Expenses
						</div>
					</div>
					<div class="segmentedBar">
						<div
							class="segment"
							:class="{ selected: userWantsToExport === 'thisMonth' }"
							@click="userClickedThisMonthTab"
						>
							<div class="title">
								This Month
							</div>
						</div>
						<div class="separator separator1" />
						<div
							class="segment"
							:class="{ selected: userWantsToExport === 'lastMonth' }"
							@click="userClickedLastMonthTab"
						>
							<div class="title">
								Last Month
							</div>
						</div>
					</div>
					<section class="inputGroup">
						<div class="block" type="button" @click="showStartDate">
							<span class="textCell">From</span>
							<span class="selectedItem">{{ userSelectedStartDate }}</span>
							<font-awesome-icon
								:icon="['fas', 'chevron-right']"
								class="cellIcon"
							/>
						</div>
						<div class="block" type="button" @click="showEndDate">
							<span class="textCell">To</span>
							<span class="selectedItem">{{ userSelectedEndDate }}</span>
							<font-awesome-icon
								:icon="['fas', 'chevron-right']"
								class="cellIcon"
							/>
						</div>
					</section>
					<section class="inputGroup">
						<div
							class="block"
							type="button"
							@click="showField('category', 'Select Category')"
						>
							<span class="textCell">Category</span>
							<span class="selectedItem">{{ userChoiceOfCategoryName }}</span>
							<font-awesome-icon
								:icon="['fas', 'chevron-right']"
								class="cellIcon"
							/>
						</div>
						<div
							class="block"
							type="button"
							@click="showField('project', 'Select Project')"
						>
							<span class="textCell">Project</span>
							<span class="selectedItem">{{ userChoiceOfProjectName }}</span>
							<font-awesome-icon
								:icon="['fas', 'chevron-right']"
								class="cellIcon"
							/>
						</div>
						<div class="block" type="button">
							<span class="textCell">Reimbursable</span>
							<span class="selectedItem">
								<label class="switch">
									<input
										type="checkbox"
										:checked="reimbursable"
										@click="userToggleReimbursable()"
									/>
									<span class="slider round" />
								</label>
							</span>
						</div>
					</section>
					<section class="inputGroup">
						<div class="block" type="button">
							<span class="textCell">Attach Receipts</span>
							<span class="selectedItem">
								<label class="switch">
									<input
										type="checkbox"
										:checked="attachReceipts"
										@click="userToggleAttachReceipts()"
									/>
									<span class="slider round" />
								</label>
							</span>
						</div>
					</section>
					<div class="actionContainer">
						<small>Need instruction here about CSV export feature</small>
						<button class="nextButton" @click="showEmailInputModal">
							Next
						</button>
					</div>
				</section>

				<section class="rightContent" :class="{ hide: !focusingField }">
					<div v-if="focusingFieldTitle" class="navBar">
						<button class="backToExport" @click="hideFields">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
							<span class="text">Export</span>
						</button>
						<h4 class="title">
							{{ focusingFieldTitle }}
						</h4>
					</div>
					<div class="currencyList tableView expensesList">
						<RangeDatePicker
							v-if="
								focusingField === 'startDate' || focusingField === 'endDate'
							"
							id="exportDatePicker"
							:focusing-field="focusingField"
							:user-selected-start-date="userSelectedStartDate"
							:user-selected-end-date="userSelectedEndDate"
							:selected-date-in-picker="selectedDateInPicker"
							:user-pick-a-date="userPickADate"
							:highlighted-dates="highlightedDates"
						/>
						<CategoryToExport
							:focusing-field="focusingField"
							:user-choice-of-category-name="userChoiceOfCategoryName"
							:user-chose-a-category="userChoseACategory"
						/>
						<ProjectToExport
							:focusing-field="focusingField"
							:user-choice-of-project-name="userChoiceOfProjectName"
							:user-choose-a-project="userChooseAProject"
						/>
					</div>
				</section>
			</main>
		</section>
		<ExportPreviewModal
			:is-export-email-modal-open="isExportEmailModalOpen"
			:close-email-input-modal="closeEmailInputModal"
			:user-selected-start-date="userSelectedStartDate"
			:user-selected-end-date="userSelectedEndDate"
			:user-choice-of-category-name="userChoiceOfCategoryName"
			:user-choice-of-project-name="userChoiceOfProjectName"
			:attach-receipts="attachReceipts"
			:reimbursable="reimbursable"
			:user-email="userEmail"
			:user-email-input-blur="userEmailInputBlur"
			:user-email-error-message="userEmailErrorMessage"
			:cc-email="ccEmail"
			:cc-email-input-blur="ccEmailInputBlur"
			:cc-email-error-message="ccEmailErrorMessage"
			:handle-submit="handleSubmit"
			:is-loading="isSubmitLoading"
			@validateUserEmail="validateUserEmail"
			@validateCcEmail="validateCcEmail"
		/>
		<ResponseModal
			:is-submit-response="isSubmitResponse"
			:close-submit-response-modal="closeSubmitResponseModal"
			:response-error-message="responseErrorMessage"
		/>
	</div>
</template>

<script>
import moment from "moment";
import Hamburger from "../components/Hamburger.vue";
import Sidebar from "../components/Sidebar.vue";
import RangeDatePicker from "../components/Export/DatePicker.vue";
import CategoryToExport from "../components/Export/CategoryToExport.vue";
import ProjectToExport from "../components/Export/ProjectToExport.vue";
import ExportPreviewModal from "../components/Export/ExportPreviewModal.vue";
import ResponseModal from "../components/Export/ResponseModal.vue";

export default {
	components: {
		Sidebar,
		RangeDatePicker,
		CategoryToExport,
		ProjectToExport,
		ExportPreviewModal,
		ResponseModal,
		Hamburger
	},
	data() {
		return {
			firstDayOfLastMonth: moment()
				.clone()
				.subtract(1, "months")
				.startOf("month")
				.format("MMMM DD, YYYY"),
			lastDayOfLastMonth: moment()
				.clone()
				.subtract(1, "months")
				.endOf("month")
				.format("MMMM DD, YYYY"),
			firstDayOfThisMonth: moment()
				.clone()
				.startOf("month")
				.format("MMMM DD, YYYY"),
			lastDayOfThisMonth: moment()
				.clone()
				.endOf("month")
				.format("MMMM DD, YYYY"),
			selectedDateInPicker: moment()
				.clone()
				.startOf("month")
				.format("YYYY, MM, DD"),
			userSelectedStartDate: moment()
				.clone()
				.startOf("month")
				.format("MMMM DD, YYYY"),
			userSelectedEndDate: moment()
				.clone()
				.endOf("month")
				.format("MMMM DD, YYYY"),
			highlightedDates: {
				to: new Date(
					moment()
						.clone()
						.endOf("month")
						.format("YYYY, MM, DD")
				),
				from: new Date(
					moment()
						.clone()
						.startOf("month")
						.format("YYYY, MM, DD")
				)
			},
			isExportEmailModalOpen: false,
			isSubmitResponse: false,
			responseErrorMessage: "",
			focusingField: "",
			focusingFieldTitle: "",
			userWantsToExport: "thisMonth",
			userChoiceOfCategoryName: "All",
			userChoiceOfProjectName: "All",
			reimbursable: false,
			attachReceipts: false,
			emailPattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			userEmail: "",
			isValidUserEmail: false,
			userEmailErrorMessage: "",
			ccEmail: "",
			isValidCcEmail: false,
			ccEmailErrorMessage: "",
			isSubmitLoading: false
		};
	},

	methods: {
		showEmailInputModal() {
			this.isExportEmailModalOpen = true;
			setTimeout(() => {
				document.getElementById("emailToSend").focus();
			}, 300);
		},
		closeEmailInputModal() {
			this.isExportEmailModalOpen = false;
			this.userEmail = "";
			this.ccEmail = "";
			this.userEmailErrorMessage = "";
			this.ccEmailErrorMessage = "";
		},
		async handleSubmit(event) {
			event.preventDefault();
			if (!this.userEmail) {
				this.userEmailErrorMessage = "Email is required";
				return;
			}
			if (!this.userEmailErrorMessage) {
				try {
					this.responseErrorMessage = "";
					this.isSubmitLoading = true;
					const paramObject = {
						from_date: moment(
							this.userSelectedStartDate,
							"MMMM DD, YYYY"
						).format("YYYYMMDD"),
						to_date: moment(this.userSelectedEndDate, "MMMM DD, YYYY").format(
							"YYYYMMDD"
						),
						category:
							this.userChoiceOfCategoryName === "All"
								? this.userChoiceOfCategoryName.toUpperCase()
								: this.userChoiceOfCategoryName,
						project:
							this.userChoiceOfProjectName === "All"
								? this.userChoiceOfProjectName.toUpperCase()
								: this.userChoiceOfProjectName,
						reimbursable: this.reimbursable ? 1 : 0,
						attach_receipts: this.attachReceipts ? 1 : 0,
						to: this.userEmail,
						cc: this.ccEmail
					};
					await this.$store.dispatch("exportExpense", paramObject);
				} catch (error) {
					this.responseErrorMessage = "Something went wrong. Please try agian.";
				} finally {
					this.isSubmitLoading = false;
					this.isExportEmailModalOpen = false;
					this.isSubmitResponse = true;
					this.userEmail = "";
					this.ccEmail = "";
				}
				return;
			}
			this.userEmailErrorMessage = "Invalid Email";
		},
		closeSubmitResponseModal() {
			this.isSubmitResponse = false;
		},
		showField(fieldName, fieldTitle) {
			this.focusingField = fieldName;
			this.focusingFieldTitle = fieldTitle;
		},
		hideFields() {
			this.focusingField = "";
			this.focusingFieldTitle = "";
		},
		showStartDate() {
			this.showField("startDate", "Select Range");
			this.selectedDateInPicker =
				this.userSelectedStartDate ||
				(this.userWantsToExport === "thisMonth"
					? moment()
							.clone()
							.startOf("month")
							.format("YYYY, MM, DD")
					: moment()
							.clone()
							.subtract(1, "months")
							.startOf("month")
							.format("YYYY, MM, DD"));
		},
		showEndDate() {
			this.showField("endDate", "Select Range");
			this.selectedDateInPicker =
				this.userSelectedStartDate ||
				(this.userWantsToExport === "thisMonth"
					? moment()
							.clone()
							.endOf("month")
							.format("YYYY, MM, DD")
					: moment()
							.clone()
							.subtract(1, "months")
							.endOf("month")
							.format("YYYY, MM, DD"));
		},
		userClickedThisMonthTab() {
			this.showField("startDate", "Select Range");
			this.userWantsToExport = "thisMonth";
			this.userSelectedStartDate = this.firstDayOfThisMonth;
			this.userSelectedEndDate = this.lastDayOfThisMonth;
			this.selectedDateInPicker = this.firstDayOfThisMonth;
			this.highlightedDates = {
				to: new Date(
					moment(this.lastDayOfThisMonth, "MMMM DD, YYYY").format(
						"YYYY, MM, DD"
					)
				),
				from: new Date(
					moment(this.userSelectedStartDate, "MMMM DD, YYYY").format(
						"YYYY, MM, DD"
					)
				)
			};
		},
		userClickedLastMonthTab() {
			this.showField("startDate", "Select Range");
			this.userWantsToExport = "lastMonth";
			this.userSelectedStartDate = this.firstDayOfLastMonth;
			this.userSelectedEndDate = this.lastDayOfLastMonth;
			this.selectedDateInPicker = this.firstDayOfLastMonth;
			this.highlightedDates = {
				to: new Date(
					moment(this.lastDayOfLastMonth, "MMMM DD, YYYY").format(
						"YYYY, MM, DD"
					)
				),
				from: new Date(
					moment(this.firstDayOfLastMonth, "MMMM DD, YYYY").format(
						"YYYY, MM, DD"
					)
				)
			};
		},
		userPickADate(date) {
			const formatedDate = moment(date).format("MMMM DD, YYYY");
			if (this.userSelectedStartDate && this.userSelectedEndDate) {
				this.userSelectedStartDate = formatedDate;
				if (
					moment(this.userSelectedStartDate).isSame(this.firstDayOfThisMonth) &&
					!this.userSelectedEndDate
				) {
					this.userWantsToExport = "thisMonth";
				} else if (
					moment(this.userSelectedStartDate).isSame(this.firstDayOfLastMonth) &&
					!this.userSelectedEndDate
				) {
					this.userWantsToExport = "lastMonth";
				} else {
					this.userWantsToExport = "custom";
				}
				this.userSelectedEndDate = "";
				this.highlightedDates = {
					to: null,
					from: null
				};
				this.showField("endDate", "Select Range");
				return;
			}
			if (this.userSelectedStartDate && this.userSelectedEndDate === "") {
				if (moment(formatedDate).isBefore(this.userSelectedStartDate)) {
					this.userSelectedEndDate = this.userSelectedStartDate;
					this.userSelectedStartDate = formatedDate;
				} else {
					this.userSelectedEndDate = formatedDate;
				}

				if (
					moment(this.userSelectedStartDate).isSame(this.firstDayOfThisMonth) &&
					moment(this.userSelectedEndDate).isSame(this.lastDayOfThisMonth)
				) {
					this.userWantsToExport = "thisMonth";
				} else if (
					moment(this.userSelectedStartDate).isSame(this.firstDayOfLastMonth) &&
					moment(this.userSelectedEndDate).isSame(this.lastDayOfLastMonth)
				) {
					this.userWantsToExport = "lastMonth";
				} else {
					this.userWantsToExport = "custom";
				}
				this.highlightedDates = {
					to: new Date(
						moment(this.userSelectedEndDate, "MMMM DD, YYYY").format(
							"YYYY, MM, DD"
						)
					),
					from: new Date(
						moment(this.userSelectedStartDate, "MMMM DD, YYYY").format(
							"YYYY, MM, DD"
						)
					)
				};
			}
		},
		userChoseACategory(category, event) {
			if (
				category.Name !== "All" &&
				event.target.classList.contains("userSelectedCategory")
			) {
				event.target.classList.remove("userSelectedCategory");
				this.userChoiceOfCategoryName = "All";
				return;
			}
			event.target.classList.add("userSelectedCategory");
			this.userChoiceOfCategoryName = category.Name;
		},
		userChooseAProject(project, event) {
			if (
				project.Name !== "All" &&
				event.target.classList.contains("userSelectedProject")
			) {
				event.target.classList.remove("userSelectedProject");
				this.userChoiceOfProjectName = "All";
				return;
			}
			event.target.classList.add("userSelectedProject");
			this.userChoiceOfProjectName = project.Name;
		},
		userToggleReimbursable() {
			this.reimbursable = !this.reimbursable;
		},
		userToggleAttachReceipts() {
			this.attachReceipts = !this.attachReceipts;
		},
		validateUserEmail(event) {
			this.userEmail = event.target.value;
			this.isValidUserEmail = this.emailPattern.test(this.userEmail);
			this.userEmailErrorMessage = "";
		},
		userEmailInputBlur() {
			setTimeout(() => {
				if (this.isExportEmailModalOpen && this.userEmail) {
					this.isValidUserEmail = this.emailPattern.test(this.userEmail);
					this.userEmailErrorMessage = this.emailPattern.test(this.userEmail)
						? ""
						: "Invalid Email";
				}
			}, 200);
		},
		validateCcEmail(event) {
			this.ccEmail = event.target.value;
			this.isValidCcEmail = this.emailPattern.test(this.ccEmail);
			this.ccEmailErrorMessage = "";
		},
		ccEmailInputBlur() {
			setTimeout(() => {
				if (this.isExportEmailModalOpen && this.ccEmail) {
					this.isValidUserEmail = this.emailPattern.test(this.ccEmail);
					this.ccEmailErrorMessage = this.emailPattern.test(this.ccEmail)
						? ""
						: "Invalid Email";
				}
			}, 200);
		}
	}
};
</script>
